import React, { useEffect, useRef } from 'react'
import { View, Animated } from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button } from './base'

type Option = {
  value: string
  text: string
}

interface ToggleSwitchProps {
  options: Option[]
  onSelect: (option: Option) => void
  selectedOption?: Option
  fullWidth?: boolean
}

export const ToggleSwitch = ({ options = [], selectedOption, onSelect }: ToggleSwitchProps) => {
  const styles = useStyleSheet(themedStyles)
  const animatedLeft = useRef(new Animated.Value(0)).current
  const animatedWidth = useRef(new Animated.Value(0)).current
  const optionLayouts = useRef<{ [key: string]: { x: number; width: number } }>({})
  const isInitialized = useRef(false)

  useEffect(() => {
    const layout = selectedOption && optionLayouts.current[selectedOption.value]
    if (layout) {
      Animated.parallel([
        Animated.spring(animatedLeft, {
          toValue: layout.x,
          useNativeDriver: false,
          friction: 8,
          tension: 40,
        }),
        Animated.spring(animatedWidth, {
          toValue: layout.width,
          useNativeDriver: false,
          friction: 8,
          tension: 40,
        }),
      ]).start()
    }
  }, [selectedOption, animatedLeft, animatedWidth])

  const handleLayout = (option: Option, event: any) => {
    const { x, width } = event.nativeEvent.layout
    optionLayouts.current[option.value] = { x, width }

    // Initialize animation on first selected option
    if (option.value === selectedOption?.value && !isInitialized.current) {
      animatedLeft.setValue(x)
      animatedWidth.setValue(width)
      isInitialized.current = true
    }
  }

  const handlePress = (option: Option) => {
    if (option.value === selectedOption?.value) {
      return
    }

    onSelect(option)
  }

  return (
    <View style={styles.container}>
      <Animated.View
        style={[
          styles.animatedBackground,
          {
            left: animatedLeft,
            width: animatedWidth,
          },
        ]}
      />
      {options.map((option) => (
        <Button
          key={option.value}
          type="transparent"
          size="small"
          accessibilityLabel={option.text}
          onPress={() => handlePress(option)}
          onLayout={(event) => handleLayout(option, event)}
          style={styles.option}
        >
          {option.text}
        </Button>
      ))}
    </View>
  )
}

const themedStyles = StyleService.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 4,
    backgroundColor: 'theme.surface.base1',
    borderRadius: 24,
    height: 42,
  },
  animatedBackground: {
    position: 'absolute',
    height: 34,
    backgroundColor: 'theme.background',
    borderRadius: 24,
    top: 4,
  },
  option: {
    paddingVertical: 8,
    paddingHorizontal: 8,
    color: 'theme.text.primary',
    borderRadius: 24,
    height: 34,
    backgroundColor: 'theme.transparent',
    flex: 1,
    textAlign: 'center',
  },
})
